<template>
  <div class="vfp">
    <div class="vfp-bgArea" @dragover="setActive" @dragleave="cancelActive" @drop="fileAdded">
      <div class="vfp-iconHolder vfp-gridItem">
        <slot v-if="!isLoading" name="icon">
          <svg height="40" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 32h12v-12h8l-14-14-14 14h8zm-8 4h28v4h-28z" fill="#CACFD2"></path>
          </svg>
        </slot>
        <slot v-if="isLoading" name="icon">
          <div class="loading-spinner text-center">
            <div class="spinner-border spinner-size" role="status">
              <span class="visually-hidden fs-1">{{ $t('key_loading') }}</span>
            </div>
          </div>
        </slot>
      </div>
      <input
        v-if="!isLoading"
        id="vfp-filePicker"
        ref="fileRef"
        class="vfp-inputfile vfp-gridItem"
        type="file"
        name="vfp-filePicker"
        :accept="acceptExtensions"
        :multiple="multiple"
        @change="handleFilesChange"
      />
      <label class="vfp-label vfp-gridItem" for="vfp-filePicker">
        <slot v-if="!isLoading" name="label">
          <strong>{{ $t('key_new') }}</strong>
        </slot>
        <slot v-if="isLoading" name="label">
          <strong>{{ $t('key_uploading') }}</strong>
        </slot>
      </label>
    </div>
  </div>
</template>

<script>

  import { ref } from 'vue';

  export default {
    name: 'FileSelector',
    props: {
      multiple: {
        type: Boolean,
        default: false,
      },

      // isLoading: {
      //   type: Boolean,
      //   default: false,
      // },

      acceptExtensions: {
        type: String,
        default: '',
      },

      maxFileSize: { // in bytes
        type: Number,
        default: NaN,
      },

      height: {
        type: Number,
        default: NaN,
      },

      validateFn: {
        type: Function,
        default: () => true,
      },
      errors: {
        type: Object,
        default: () => {
          return null;
        },
      },
      success: {
        type: Object,
        default: () => {
          return null;
        },
      }
    },
    emits: ['validated', 'changed'],
    setup() {
      const isDragEnter = ref(false);

      return {
        isDragEnter,
      };
    },
    data() {
      return {
        gallery: [],
        isLoading: false,
        isError: false,
        showDeleteButton: false
      }
    },
    watch: {
      errors() {
        if(this.errors) {
          this.isLoading = false
          this.isError = true
        }
      },
      success() {
        if(this.success) {
          this.isLoading = false
          this.isError = false
        }
      },
    },
    methods: {
      handleFilesChange(e) {
        if (this.isLoading) {
          e.preventDefault()
          return
        }
        const el = e.target //as HTMLInputElement;
        this.isLoading = true
        this.isError = false

        if (!el['files'] || el['files'].length === 0) {
          this.isLoading = false
          this.isError = true
          return;
        }

        this.preprocessFiles(Array.from(el['files']));
      },

      handleDrop(e) {
        this.isDragEnter = false;

        this.preprocessFiles(Array.from(e.dataTransfer?.files || []));
      },

      preprocessFiles(files) {
        const result = this.validate(files);
        this.$emit('validated', result, files);
        // validation
        if (result === true) {
          this.$emit('changed', files[0]);
        } else {
          this.isLoading = false
          this.isError = true
        }

        // clear selected files
        const fileRef = this.$refs.fileRef //as HTMLInputElement;

        if (fileRef) {
          fileRef.value = '';
        }
      },

      validate(files) {
        // file selection
        if (!this.multiple && files.length > 1) {
          return 'MULTIFILES_ERROR';
        }

        // extension
        if (!this.checkFileExtensions(files)) {
          return 'EXTENSION_ERROR';
        }

        // // file size
        // if (!this.checkFileSize(files)) {
        //   return 'FILE_SIZE_ERROR';
        // }

        // custom validation
        return this.validateFn(files);
      },

      checkFileExtensions(files) {
        // get non-empty, unique extension items
        const extList = [...new Set(
          this.acceptExtensions.toLowerCase()
            .split(',')
            .filter(Boolean),
        )];
        
        if (extList.length == 0) {
          return true
        }
        // check if the selected files are in supported extensions
        const invalidFileIndex = files.findIndex(file => {
          const ext = `.${file.name.toLowerCase().split('.').pop()}`;

          return !extList.includes(ext);
        });

        // all exts are valid
        return invalidFileIndex === -1;
      },

      checkFileSize(files) {
        if (Number.isNaN(this.maxFileSize)) {
          return true;
        }

        // find invalid file size
        const invalidFileIndex = files.findIndex(file => file.size > this.maxFileSize);

        // all file size are valid
        return invalidFileIndex === -1;
      },
    },
  };

</script>

<style>

  .vfp {
    display: flex;
    height: 100px;

    .vfp-bgArea {
      transition: 0.3s;
      background: #F2F3F4;
      display: grid;
      grid-template-rows: 60% 40%;
      padding: 25px 10px;
      width: 154px !important;
      outline: 2px dashed #CACFD2;
      outline-offset: -10px;
      color: #3b3e40;
      text-align: center;

      height: 100%;
      width: 180px;
    }

    .vfp-inputfile {
      position: absolute;
      height: 40%;
      overflow: hidden;
      width: 16%;
      opacity: 0;
      cursor: pointer;
    }

    .vfp-gridItem {
      align-self: center;
      justify-self: center;
    }

    .vfp-label {
      cursor: pointer;
      text-align: center;
      font-size: 0.9rem;
    }

    .vfp-active {
      background-color: #D7DBDD;
      outline-color: #F2F3F4;
    }

    @media only screen and (max-width: 440px) {
      .vfp-bgArea {
        padding: 18px 10px;
        grid-template-rows: 50% 50%;
        grid-row-gap: 5px;
      }
    }
  }

</style>